import type {
  ProjectSnapshot,
  SyncMutation,
  Task,
  TaskListItem,
  TaskListUser,
  TaskRender,
} from "@relais/shared";

// Distributive Omit pour préserver l'union discriminée à travers Omit<...>.
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;
// base_rev exclu : il n'est plus stampé qu'au flush (dernier rev acké) — le
// type garantit qu'aucun appelant ne peut en fournir un périmé.
export type EnqueuePayload = DistributiveOmit<
  SyncMutation,
  "client_mutation_id" | "created_at" | "base_rev"
>;
import { nanoid } from "nanoid";
import { getDb, type OfflineQueueRecord, type RelaisOfflineDb } from "./db.js";
import { NOOP_OBSERVER, type SyncObserver } from "./observer.js";
import { acquireLock, releaseLock } from "./persist-lock.js";
import {
  MAX_RETRY,
  MAX_UPLOAD_RETRY,
  MUTATION_PRIORITY,
  STALE_PROCESSING_MS,
  inUploadBackoff,
  type FlushOptions,
  type FlushRejection,
  type FlushResult,
  type SyncTransport,
  type UploadError,
  type UploadSweepResult,
} from "./types.js";

const CATALOGUE_TTL_MS = 60 * 60 * 1000; // 1h front-side

// Marqueur des update_render purgés par la re-compaction : jamais ressuscités
// par retrySyncForTask (un snapshot supersédé qui repartirait pending avec un
// updatedAt frais gagnerait la re-compaction suivante contre le vrai dernier
// snapshot — vieux payload envoyé, draft récent perdu).
const SUPERSEDED_ERROR = "superseded by newer local snapshot";

// Lot du dégonflage rétroactif (reclaimUploadedBlobs). Chaque ligne coûte un
// `get` d'un enregistrement potentiellement lourd : assez petit pour rester
// loin des 5 s de withTimeout même sur une base en souffrance, assez grand pour
// qu'un appareil chargé (~800 photos) soit dégonflé en une poignée de cycles.
const RECLAIM_BATCH_SIZE = 25;

function mimeToExt(mime: string): string {
  if (mime === "image/jpeg" || mime === "image/jpg") return "jpg";
  if (mime === "image/png") return "png";
  if (mime === "image/webp") return "webp";
  if (mime === "image/gif") return "gif";
  if (mime === "image/heic") return "heic";
  if (mime === "image/heif") return "heif";
  // Vidéos SAV : l'extension compose le chemin de stockage, et le bucket la
  // lit pour servir le bon Content-Type au lecteur de la revue admin.
  if (mime === "video/mp4") return "mp4";
  if (mime === "video/quicktime") return "mov";
  if (mime === "video/webm") return "webm";
  if (mime === "video/3gpp") return "3gp";
  return "bin";
}

function base64ToBlob(base64: string, mime: string): Blob {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

// Transition de statut induite par une mutation. Pure : utilisée à la fois
// pour l'application optimiste et pour rejouer les mutations en vol par-dessus
// un snapshot serveur (cf. upsertTasks et markCompleted).
// Renvoie la même référence de Task si la mutation ne mute pas l'entité.
function applyMutationToTask(task: Task, m: SyncMutation): Task {
  switch (m.kind) {
    case "accept":
      return { ...task, status: "accepted", updated_at: m.created_at };
    case "refuse":
      return {
        ...task,
        status: "refused",
        refusal_reason: m.reason,
        updated_at: m.created_at,
      };
    case "start":
      return { ...task, status: "in_progress", updated_at: m.created_at };
    case "resume":
      return { ...task, status: "in_progress", updated_at: m.created_at };
    case "submit":
      // Stamp `submitted_at` optimistically too, not just `status`: it's the
      // field poseurs-front's lock check (isPvTaskLocked) reads, so an
      // offline submit must lock the task locally right away instead of
      // waiting for server confirmation. Idempotent — replaying this over an
      // already-submitted server task (markCompleted/upsertTasks) just
      // reasserts the same shape.
      return {
        ...task,
        status: "submitted",
        submitted_at: m.created_at,
        updated_at: m.created_at,
      };
    default:
      return task;
  }
}

export type ServiceInit = {
  userId: string;
  transport: SyncTransport;
  observer?: SyncObserver;
};

export class OfflineSyncService {
  private readonly db: RelaisOfflineDb;
  private flushing = false;

  constructor(
    public readonly userId: string,
    private readonly transport: SyncTransport,
    private readonly observer: SyncObserver = NOOP_OBSERVER,
  ) {
    this.db = getDb();
  }

  static async init(opts: ServiceInit): Promise<OfflineSyncService> {
    const svc = new OfflineSyncService(
      opts.userId,
      opts.transport,
      opts.observer ?? NOOP_OBSERVER,
    );
    await svc.db.ensureOpen();
    return svc;
  }

  // ── Reads ────────────────────────────────────────────────────────────
  async listTasks(): Promise<Task[]> {
    const rows = await this.db.withRetry(() =>
      this.db.tasks.where("userId").equals(this.userId).toArray(),
    );
    return rows.map((r) => r.data);
  }

  async getTask(id: string): Promise<Task | null> {
    const row = await this.db.withRetry(() => this.db.tasks.get(id));
    return row?.data ?? null;
  }

  // Référent (superviseur) mis en cache depuis le snapshot liste. Disponible
  // hors-ligne, contrairement au TaskDetail.supervisor qui exige le réseau.
  async getCachedSupervisor(id: string): Promise<TaskListUser | null> {
    const row = await this.db.withRetry(() => this.db.tasks.get(id));
    return row?.supervisor ?? null;
  }

  async queueLength(): Promise<number> {
    return this.db.withRetry(() =>
      this.db.queue
        .where("[userId+status]")
        .equals([this.userId, "pending"])
        .count(),
    );
  }

  async listConflicts(): Promise<Task[]> {
    const rows = await this.db.withRetry(() =>
      this.db.tasks
        .where("[userId+localStatus]")
        .equals([this.userId, "conflict"])
        .toArray(),
    );
    return rows.map((r) => r.data);
  }

  // ── Snapshot serveur → local ─────────────────────────────────────────
  // Accepte des Task « nus » (réponses flush / refresh CRM) comme des
  // TaskListItem (snapshot liste, référent joint). Le référent est mis en
  // cache et préservé d'un write à l'autre : un payload nu retombe sur la
  // valeur déjà stockée plutôt que de l'effacer.
  async upsertTasks(tasks: Array<Task | TaskListItem>): Promise<void> {
    if (tasks.length === 0) return;
    const now = new Date().toISOString();
    const taskIds = tasks.map((t) => t.id);

    // Récupère les mutations encore en vol (pending/processing) pour rejouer
    // l'état optimiste par-dessus le snapshot serveur. Sans ce replay, la loop
    // de refresh annulerait toute transition non encore flushée.
    const inflight = await this.db.withRetry(() =>
      this.db.queue
        .where("taskId")
        .anyOf(taskIds)
        .and(
          (q) =>
            q.userId === this.userId &&
            (q.status === "pending" || q.status === "processing"),
        )
        .sortBy("priority"),
    );
    const byTask = new Map<string, OfflineQueueRecord[]>();
    for (const m of inflight) {
      const arr = byTask.get(m.taskId) ?? [];
      arr.push(m);
      byTask.set(m.taskId, arr);
    }

    // Référent déjà en cache, pour préserver lors d'un upsert nu.
    const existing = await this.db.withRetry(() =>
      this.db.tasks.bulkGet(taskIds),
    );
    const cachedSupervisor = new Map<string, TaskListUser>();
    for (const row of existing) {
      if (row?.supervisor) cachedSupervisor.set(row.id, row.supervisor);
    }

    await this.db.withRetry(() =>
      this.db.tasks.bulkPut(
        tasks.map((serverTask) => {
          const mutations = byTask.get(serverTask.id) ?? [];
          const replayed = mutations.reduce(
            (t, m) => applyMutationToTask(t, m.payload),
            serverTask as Task,
          );
          const supervisor =
            (serverTask as TaskListItem).supervisor ??
            cachedSupervisor.get(serverTask.id);
          return {
            id: serverTask.id,
            userId: this.userId,
            data: replayed,
            ...(supervisor ? { supervisor } : {}),
            updatedAt: now,
            localStatus:
              mutations.length > 0 ? ("dirty" as const) : ("clean" as const),
          };
        }),
      ),
    );
  }

  async refreshFromServer(since?: string): Promise<TaskListItem[]> {
    const server = await this.transport.fetchTasks(since);
    await this.upsertTasks(server);
    // Snapshot complet uniquement (pas de `since`) : une tâche locale absente
    // de la réponse n'est plus visible pour cet utilisateur (dé-assignation
    // admin, annulation…). Le render est déjà côté serveur : on purge tout le
    // local — tâche, render, medias, mutations en file.
    if (since === undefined) {
      await this.purgeTasksAbsentFromSnapshot(server);
    }
    return server;
  }

  private async purgeTasksAbsentFromSnapshot(
    server: Array<Task | TaskListItem>,
  ): Promise<void> {
    const serverIds = new Set(server.map((t) => t.id));
    const localIds = (await this.db.withRetry(() =>
      this.db.tasks.where("userId").equals(this.userId).primaryKeys(),
    )) as string[];
    const removed = localIds.filter((id) => !serverIds.has(id));
    if (removed.length === 0) return;

    await this.db.withRetry(async () => {
      await this.db.tasks.bulkDelete(removed);
      await this.db.renders
        .where("taskId")
        .anyOf(removed)
        .and((r) => r.userId === this.userId)
        .delete();
      await this.db.medias
        .where("taskId")
        .anyOf(removed)
        .and((m) => m.userId === this.userId)
        .delete();
      await this.db.queue
        .where("taskId")
        .anyOf(removed)
        .and((q) => q.userId === this.userId)
        .delete();
    });
    this.observer.onTasksPurged?.({ taskIds: removed });
  }

  // Met à jour le project_snapshot d'une tâche locale (utilisé pour rafraîchir
  // les données CRM côté conducteur à l'ouverture de la tâche). Passe par
  // upsertTasks pour préserver le replay des mutations en vol.
  async updateTaskSnapshot(
    taskId: string,
    snapshot: ProjectSnapshot,
  ): Promise<Task | null> {
    const task = await this.getTask(taskId);
    if (!task) return null;
    const next: Task = { ...task, project_snapshot: snapshot };
    await this.upsertTasks([next]);
    return this.getTask(taskId);
  }

  async refreshCatalogue(
    name: string,
    opts: { force?: boolean } = {},
  ): Promise<void> {
    if (!opts.force) {
      const existing = await this.db.withRetry(() =>
        this.db.catalogues.get(name),
      );
      if (
        existing &&
        Date.now() - Date.parse(existing.fetchedAt) < CATALOGUE_TTL_MS
      ) {
        return;
      }
    }
    try {
      const remote = await this.transport.fetchCatalogue(name);
      await this.db.withRetry(() =>
        this.db.catalogues.put({
          name: remote.name,
          version: remote.version,
          fetchedAt: remote.fetchedAt,
          data: remote.options,
        }),
      );
    } catch (err) {
      console.warn(`[catalogue:${name}] refresh failed`, err);
    }
  }

  async getCatalogue<T>(
    name: string,
  ): Promise<{ options: T[]; fetchedAt: string } | null> {
    const row = await this.db.withRetry(() => this.db.catalogues.get(name));
    return row ? { options: row.data as T[], fetchedAt: row.fetchedAt } : null;
  }

  // ── Enqueue ──────────────────────────────────────────────────────────
  async enqueue(mutation: EnqueuePayload): Promise<string> {
    // Compaction : `update_render` est un snapshot complet et debouncé toutes
    // les 400ms côté UI. Si une entrée `pending` existe déjà pour la même
    // tâche, on remplace son payload au lieu d'empiler une nouvelle ligne.
    // Le `client_mutation_id` est conservé : l'idempotence serveur reste
    // intacte (l'ID identifie un slot d'intention, pas une révision).
    if (mutation.kind === "update_render") {
      const existing = await this.db.withRetry(() =>
        this.db.queue
          .where("[userId+status]")
          .equals([this.userId, "pending"])
          .and((q) => q.taskId === mutation.task_id && q.kind === "update_render")
          .first(),
      );
      if (existing) {
        const stamp = new Date().toISOString();
        const compactedPayload = {
          ...existing.payload,
          payload: mutation.payload,
        } as SyncMutation;
        await this.db.withRetry(() =>
          this.db.queue.update(existing.id, {
            payload: compactedPayload,
            retryCount: 0,
            lastError: undefined,
            updatedAt: stamp,
          }),
        );
        await this.applyOptimistically(compactedPayload);
        this.observer.onEnqueue?.({
          kind: mutation.kind,
          taskId: mutation.task_id,
        });
        return existing.id;
      }
    }

    if (mutation.kind === "submit") {
      await this.healMissingTransition(mutation.task_id);
    }

    const full = {
      ...mutation,
      client_mutation_id: nanoid(16),
      created_at: new Date().toISOString(),
    } as SyncMutation;
    const id = nanoid(16);
    const record: OfflineQueueRecord = {
      id,
      userId: this.userId,
      taskId: full.task_id,
      kind: full.kind,
      payload: full,
      priority: MUTATION_PRIORITY[full.kind],
      status: "pending",
      retryCount: 0,
      createdAt: full.created_at,
      updatedAt: full.created_at,
    };
    await this.db.withRetry(() => this.db.queue.add(record));
    await this.applyOptimistically(full);
    this.observer.onEnqueue?.({ kind: full.kind, taskId: full.task_id });
    return id;
  }

  // ── Application optimiste (avant flush) ──────────────────────────────
  private async applyOptimistically(m: SyncMutation): Promise<void> {
    const task = await this.getTask(m.task_id);
    if (!task) return;

    const transitioned = applyMutationToTask(task, m);
    // Pour les mutations qui ne transitionnent pas la tâche (register_media,
    // remove_media, update_render), on bumpe updated_at pour signaler une
    // activité locale dans la liste.
    const next: Task =
      transitioned === task
        ? { ...task, updated_at: new Date().toISOString() }
        : transitioned;
    await this.db.withRetry(() =>
      this.db.tasks.put({
        id: task.id,
        userId: this.userId,
        data: next,
        updatedAt: next.updated_at,
        localStatus: "dirty",
      }),
    );

    if (m.kind === "update_render") {
      const existing = await this.db.withRetry(() => this.db.renders.get(m.task_id));
      const stamp = new Date().toISOString();
      await this.db.withRetry(() =>
        this.db.renders.put({
          taskId: m.task_id,
          userId: this.userId,
          data: {
            ...(existing?.data ?? {
              id: `local:${m.task_id}`,
              task_id: m.task_id,
              payload: {},
              payload_rev: 0,
              submitted_at: null,
              review_status: "pending",
              review_notes: null,
              reviewed_by: null,
              reviewed_at: null,
              created_at: stamp,
              updated_at: stamp,
            }),
            payload: m.payload,
            updated_at: stamp,
          },
          updatedAt: stamp,
        }),
      );
    }
  }

  // ── Media upload (background sweep) ──────────────────────────────────
  // Uploade les blobs Dexie pending pour les tâches dont le submit est
  // déjà enqueued, ainsi que pour les tâches déjà `submitted` côté snapshot
  // local (édition du rendu après soumission : aucune mutation submit n'est
  // ré-enqueuée, mais les nouveaux médias doivent quand même partir).
  // Enqueue un register_media par upload réussi et marque la ligne
  // uploaded=true. En cas d'échec, incrémente uploadRetryCount et pose
  // uploadLastAttemptAt pour le backoff.
  /**
   * Tâches dont les photos peuvent effectivement partir : soumises localement,
   * ou portant un `submit` encore en file. Le serveur verrouille le rendu dès
   * qu'il est terminal (`assertRenderEditable`), donc une photo rattachée à une
   * mission clôturée ne partira jamais — l'annoncer « à envoyer » est un
   * mensonge, et la balayer est du travail perdu.
   */
  private async uploadableTaskIds(): Promise<string[]> {
    const submitPending = await this.db.withRetry(() =>
      this.db.queue
        .where("[userId+status]")
        .equals([this.userId, "pending"])
        .and((q) => q.kind === "submit")
        .toArray(),
    );
    const submittedLocal = await this.db.withRetry(() =>
      this.db.tasks
        .where("userId")
        .equals(this.userId)
        .and((t) => t.data.status === "submitted")
        .toArray(),
    );
    return [
      ...new Set([
        ...submitPending.map((q) => q.taskId),
        ...submittedLocal.map((t) => t.id),
      ]),
    ];
  }

  /**
   * Photos qui attendent vraiment de partir, pour la bannière de sync.
   *
   * L'ancien compteur balayait TOUTES les médias de l'utilisateur avec un
   * `.and()` — donc désérialisait chaque blob — à chaque tick de cycle. Sur un
   * appareil chargé il expirait en boucle et, pire, monopolisait IndexedDB en
   * concurrence du dégonflage censé le soigner. Il annonçait au passage des
   * photos de missions clôturées, que le serveur refuse : un relais pouvait
   * appuyer sur « Synchroniser » indéfiniment sans que le compteur bouge.
   */
  async pendingUploadCount(): Promise<number> {
    const taskIds = await this.uploadableTaskIds();
    if (taskIds.length === 0) return 0;
    return this.db.withRetry(() =>
      this.db.medias
        .where("taskId")
        .anyOf(taskIds)
        .and((m) => m.userId === this.userId && !m.uploaded && !!m.blobData)
        .count(),
    );
  }

  async uploadPendingForSubmittedTasks(): Promise<UploadSweepResult> {
    if (!acquireLock(`upload:${this.userId}`)) {
      return { uploaded: [], errored: [] };
    }
    try {
      const taskIds = await this.uploadableTaskIds();

      const uploaded: string[] = [];
      const errored: UploadError[] = [];

      for (const taskId of taskIds) {
        const pending = await this.db.withRetry(() =>
          this.db.medias
            .where("taskId")
            .equals(taskId)
            .and(
              (m) =>
                m.userId === this.userId &&
                !m.uploaded &&
                !!m.blobData &&
                (m.uploadRetryCount ?? 0) < MAX_UPLOAD_RETRY &&
                !inUploadBackoff(m),
            )
            .toArray(),
        );

        for (const m of pending) {
          try {
            const ext = mimeToExt(m.mimeType);
            const handle = await this.transport.createSignedUpload(
              taskId,
              m.clientId,
              ext,
            );
            const blob = base64ToBlob(m.blobData!, m.mimeType);
            await this.transport.uploadBlob(handle.signed_url, blob, m.mimeType);

            await this.enqueue({
              kind: "register_media",
              task_id: taskId,
              media: {
                client_id: m.clientId,
                kind: m.kind,
                storage_path: handle.storage_path,
                mime_type: m.mimeType,
                size_bytes: m.sizeBytes,
                ...(m.caption ? { caption: m.caption } : {}),
              },
            });

            // Le blob est CONSERVÉ ici, volontairement : à cet instant le
            // fichier est dans le bucket mais `register_media` n'est
            // qu'ENQUEUÉ — rien ne le rattache encore au rendu côté serveur.
            // Tant que le serveur n'a pas confirmé cet enregistrement, la copie
            // locale reste la seule preuve exploitable, et on ne détruit pas
            // une photo de chantier sur la foi d'un PUT de stockage.
            // Libération effective dans markCompleted(), à l'accusé serveur.
            await this.db.withRetry(() =>
              this.db.medias.update(m.id, {
                uploaded: true,
                storagePath: handle.storage_path,
                updatedAt: new Date().toISOString(),
              }),
            );

            uploaded.push(m.id);
          } catch (err) {
            const next = (m.uploadRetryCount ?? 0) + 1;
            const message = (err as Error).message;
            await this.db.withRetry(() =>
              this.db.medias.update(m.id, {
                uploadRetryCount: next,
                uploadLastError: message,
                uploadLastAttemptAt: new Date().toISOString(),
              }),
            );
            this.observer.onUploadError?.({
              taskId,
              mediaId: m.id,
              retryCount: next,
              exhausted: next >= MAX_UPLOAD_RETRY,
              message,
            });
            errored.push({ taskId, mediaId: m.id, error: message });
          }
        }
      }
      return { uploaded, errored };
    } finally {
      releaseLock(`upload:${this.userId}`);
    }
  }

  // Auto-heal : submit exige in_progress côté serveur (accepted→submitted et
  // questioned→submitted interdits). Si la transition intermédiaire a été
  // perdue (timeout Dexie, purge), on la ré-enqueue devant le submit :
  // priorité 20 < 50 garantit l'ordre dans le batch, et le serveur converge
  // (from === to → ok, dédoublonnage durable par client_mutation_id) si elle
  // avait déjà été appliquée. Jamais depuis assigned : l'accept manquant
  // n'est pas une intention devinable. Appelé à l'enqueue d'un submit
  // uniquement — pas au flush : le statut local d'une tâche avec submit
  // pending est toujours « submitted » (replay optimiste), healer sans
  // condition pourrait régresser une tâche réellement soumise.
  private async healMissingTransition(taskId: string): Promise<void> {
    const task = await this.getTask(taskId);
    const healKind =
      task?.status === "accepted"
        ? ("start" as const)
        : task?.status === "questioned"
          ? ("resume" as const)
          : null;
    if (!healKind) return;
    const alreadyQueued = await this.db.withRetry(() =>
      this.db.queue
        .where("taskId")
        .equals(taskId)
        .and(
          (q) =>
            q.userId === this.userId &&
            (q.kind === "start" || q.kind === "resume") &&
            (q.status === "pending" || q.status === "processing"),
        )
        .first(),
    );
    if (!alreadyQueued) {
      await this.enqueue({ kind: healKind, task_id: taskId });
    }
  }

  // Recovery : une ligne processing laissée par un flush interrompu ne serait
  // jamais re-flushée (on ne lit que pending) tout en étant rejouée à l'infini
  // sur l'UI par upsertTasks. Passé le seuil, elle repart pending — updatedAt
  // conservé (il date la mise en vol par le flush interrompu : tout pending
  // concurrent a forcément été enqueué après, donc reste le plus récent pour
  // la re-compaction). Ré-envoi sûr : le serveur dédoublonne durablement par
  // client_mutation_id (sync_applied_mutations) et renvoie l'état courant.
  // Transaction : les statuts sont flippés via update ciblé, jamais par
  // réécriture de copies mémoire — un enqueue/compaction concurrent (autosave
  // 400ms) ne peut pas être clobberé.
  private async recoverStaleProcessing(): Promise<void> {
    const staleCutoff = Date.now() - STALE_PROCESSING_MS;
    await this.db.withRetry(() =>
      this.db.transaction("rw", this.db.queue, async () => {
        const stale = await this.db.queue
          .where("[userId+status]")
          .equals([this.userId, "processing"])
          .and((q) => Date.parse(q.updatedAt) < staleCutoff)
          .toArray();
        for (const q of stale) {
          await this.db.queue.update(q.id, { status: "pending" as const });
        }
      }),
    );
  }

  // Re-compaction : deux update_render pending peuvent coexister pour une même
  // tâche (recovery ci-dessus, mais aussi enqueue pendant un flush en vol puis
  // markBatchFailed qui remet l'ancien en pending). Ils partiraient dans le
  // même batch avec le même base_rev → conflit garanti, et l'ordre entre eux
  // est arbitraire : le vieux snapshot pourrait gagner côté serveur. Un seul
  // part — le plus récent par updatedAt.
  private async compactPendingRenders(): Promise<void> {
    await this.db.withRetry(() =>
      this.db.transaction("rw", this.db.queue, async () => {
        const pendingRenders = await this.db.queue
          .where("[userId+status]")
          .equals([this.userId, "pending"])
          .and((q) => q.kind === "update_render")
          .toArray();
        const byTask = new Map<string, OfflineQueueRecord[]>();
        for (const q of pendingRenders) {
          const arr = byTask.get(q.taskId) ?? [];
          arr.push(q);
          byTask.set(q.taskId, arr);
        }
        for (const rows of byTask.values()) {
          if (rows.length <= 1) continue;
          const losers = rows
            .slice()
            .sort((a, b) => a.updatedAt.localeCompare(b.updatedAt))
            .slice(0, -1);
          for (const q of losers) {
            await this.db.queue.update(q.id, {
              status: "purged" as const,
              lastError: SUPERSEDED_ERROR,
              updatedAt: new Date().toISOString(),
            });
          }
        }
      }),
    );
  }

  // ── Flush ────────────────────────────────────────────────────────────
  async flush(opts: FlushOptions = {}): Promise<FlushResult> {
    const limit = opts.limit ?? 50;
    if (!acquireLock(`flush:${this.userId}`)) {
      return { processed: 0, failed: 0, conflicts: 0, completed: [], conflictTasks: [], rejections: [] };
    }
    this.flushing = true;
    try {
      await this.recoverStaleProcessing();
      await this.compactPendingRenders();

      const pending = await this.db.withRetry(() =>
        this.db.queue
          .where("[userId+status]")
          .equals([this.userId, "pending"])
          .sortBy("priority"),
      );

      if (pending.length === 0) {
        return {
          processed: 0,
          failed: 0,
          conflicts: 0,
          completed: [],
          conflictTasks: [],
          rejections: [],
        };
      }

      // Ce garde ne sert qu'à retenir les `submit` dont les photos ne sont pas
      // encore montées : on ne regarde donc QUE les tâches qui ont un submit en
      // attente, au lieu de balayer toutes les médias de l'utilisateur.
      // L'ancienne version lisait l'intégralité du store — `.and()` désérialise
      // chaque valeur, blobData compris — soit plusieurs Go par cycle sur un
      // appareil chargé : 5 s de timeout dépassées, flush jamais terminé, et
      // toute autre écriture (l'ajout d'une photo) en file derrière. C'est le
      // mécanisme exact de l'incident du 2026-09-21.
      const submitTaskIds = [
        ...new Set(
          pending.filter((b) => b.kind === "submit").map((b) => b.taskId),
        ),
      ];
      const pendingPhotoTaskIds = new Set<string>(
        submitTaskIds.length === 0
          ? []
          : (
              await this.db.withRetry(() =>
                this.db.medias
                  .where("taskId")
                  .anyOf(submitTaskIds)
                  .and((m) => m.userId === this.userId && !m.uploaded && !!m.blobData)
                  .toArray(),
              )
            ).map((m) => m.taskId),
      );

      const eligible = pending.filter(
        (b) => !(b.kind === "submit" && pendingPhotoTaskIds.has(b.taskId)),
      );

      const batch = eligible.slice(0, limit);

      if (batch.length === 0) {
        return {
          processed: 0,
          failed: 0,
          conflicts: 0,
          completed: [],
          conflictTasks: [],
          rejections: [],
        };
      }

      this.observer.onFlushStart?.({ batchSize: batch.length });

      // Claim atomique : re-lecture fraîche + marquage processing dans une
      // transaction (updatedAt bumpé : c'est lui qui date la mise en vol pour
      // le seuil de staleness de la recovery). Un autosave a pu compacter un
      // payload plus récent sur une ligne du batch entre la lecture pending et
      // ce point — un bulkPut des copies mémoire l'écraserait, et on enverrait
      // un snapshot périmé : on repart des lignes re-lues.
      const processingStamp = new Date().toISOString();
      const claimed = await this.db.withRetry(() =>
        this.db.transaction("rw", this.db.queue, async () => {
          const fresh = await this.db.queue.bulkGet(batch.map((b) => b.id));
          const rows = fresh.filter(
            (q): q is OfflineQueueRecord => q !== undefined && q.status === "pending",
          );
          for (const q of rows) {
            await this.db.queue.update(q.id, {
              status: "processing" as const,
              updatedAt: processingStamp,
            });
          }
          return rows;
        }),
      );

      if (claimed.length === 0) {
        return {
          processed: 0,
          failed: 0,
          conflicts: 0,
          completed: [],
          conflictTasks: [],
          rejections: [],
        };
      }

      // base_rev lu au plus tard, sous le lock flush : toujours après le
      // markCompleted du flush précédent, donc le snapshot part du dernier rev
      // acké. Écrase un éventuel base_rev posé à l'enqueue (anciennes lignes
      // de queue) — c'est le point qui tue la race enqueue-pendant-flush.
      const mutations: SyncMutation[] = [];
      for (const b of claimed) {
        if (b.payload.kind === "update_render") {
          const localRender = await this.db.withRetry(() =>
            this.db.renders.get(b.taskId),
          );
          const rev = localRender?.data?.payload_rev;
          mutations.push(
            typeof rev === "number" ? { ...b.payload, base_rev: rev } : b.payload,
          );
        } else {
          mutations.push(b.payload);
        }
      }
      let response: Awaited<ReturnType<SyncTransport["flush"]>>;
      try {
        response = await this.transport.flush(mutations);
      } catch (err) {
        // Erreur réseau : on remet en pending et incrémente le retry
        const message = (err as Error).message;
        await this.markBatchFailed(claimed, message);
        this.observer.onNetworkError?.({
          phase: "flush",
          message,
          batchSize: claimed.length,
        });
        return { processed: 0, failed: claimed.length, conflicts: 0, completed: [], conflictTasks: [], rejections: [] };
      }

      const completed: string[] = [];
      const conflictTasks: Task[] = [];
      const rejections: FlushRejection[] = [];
      let failed = 0;

      for (const result of response.results) {
        const b = claimed.find((x) => x.payload.client_mutation_id === result.client_mutation_id);
        if (!b) continue;
        if (result.status === "ok") {
          await this.markCompleted(b, result.task, result.render);
          completed.push(b.id);
        } else if (result.status === "conflict") {
          await this.markConflict(b, result.server_task, result.reason, result.server_render);
          conflictTasks.push(result.server_task);
          rejections.push({
            kind: b.payload.kind,
            task_id: b.taskId,
            status: "conflict",
            reason: result.reason,
          });
        } else {
          await this.markErrored(b, result.message);
          failed++;
          rejections.push({
            kind: b.payload.kind,
            task_id: b.taskId,
            status: "error",
            reason: result.message,
            code: result.code,
          });
        }
      }

      const result: FlushResult = {
        processed: response.results.length,
        failed,
        conflicts: conflictTasks.length,
        completed,
        conflictTasks,
        rejections,
      };
      this.observer.onFlushEnd?.(result);
      return result;
    } finally {
      this.flushing = false;
      releaseLock(`flush:${this.userId}`);
    }
  }

  isFlushing(): boolean {
    return this.flushing;
  }

  private async markCompleted(
    b: OfflineQueueRecord,
    serverTask: Task,
    serverRender?: TaskRender,
  ) {
    await this.db.withRetry(async () => {
      // Avance la rev sans toucher au payload : le draft local peut déjà
      // porter un snapshot pas encore flushé.
      if (serverRender) {
        const localRender = await this.db.renders.get(serverTask.id);
        if (localRender?.data) {
          await this.db.renders.put({
            ...localRender,
            data: { ...localRender.data, payload_rev: serverRender.payload_rev },
            updatedAt: new Date().toISOString(),
          });
        }
      }
      // Ligne SUPPRIMÉE, plus seulement marquée « completed » : une mutation
      // confirmée par le serveur est inerte (l'idempotence vit côté serveur,
      // dans sync_applied_mutations, pas ici). Les garder faisait croître la
      // file sans borne — 1633 lignes relevées sur un appareil terrain, chaque
      // `update_render` portant un snapshot de payload complet — et le flush
      // relit cette file à chaque cycle. On ne touche pas aux `purged` /
      // `failed` / `conflict` : retrySyncForTask() les remet en `pending`.
      await this.db.queue.delete(b.id);
      // Rejoue les mutations encore en vol pour cette tâche (autres entries
      // pending/processing) afin de ne pas annuler une transition non encore
      // confirmée par le serveur.
      const remaining = await this.db.queue
        .where("taskId")
        .equals(serverTask.id)
        .and(
          (q) =>
            q.userId === this.userId &&
            (q.status === "pending" || q.status === "processing"),
        )
        .sortBy("priority");
      const replayed = remaining.reduce(
        (t, m) => applyMutationToTask(t, m.payload),
        serverTask,
      );
      // Le payload flush est un Task nu : conserve le référent déjà en cache.
      const prev = await this.db.tasks.get(serverTask.id);
      await this.db.tasks.put({
        id: serverTask.id,
        userId: this.userId,
        data: replayed,
        ...(prev?.supervisor ? { supervisor: prev.supervisor } : {}),
        updatedAt: new Date().toISOString(),
        localStatus: remaining.length > 0 ? "dirty" : "clean",
      });
    });
    await this.releaseConfirmedBlob(b);
  }

  /**
   * Libère le base64 d'une photo dont le serveur vient d'accuser
   * l'enregistrement — le seul instant où la copie locale est prouvée
   * redondante.
   *
   * HORS du withRetry de markCompleted, délibérément : ce bloc partage un
   * budget de 5 s avec les écritures d'état (rendu, file, tâche), et lire puis
   * réécrire un enregistrement de plusieurs Mo pouvait l'y faire déborder.
   * markCompleted échouerait alors APRÈS un aller-retour serveur réussi, et la
   * mutation repartirait au cycle suivant. Ici l'échec est sans conséquence :
   * le blob reste, et reclaimUploadedBlobs() le reprendra.
   */
  private async releaseConfirmedBlob(b: OfflineQueueRecord): Promise<void> {
    if (b.payload.kind !== "register_media") return;
    const mediaId = `${b.taskId}:${b.payload.media.client_id}`;
    try {
      const row = await this.db.withRetry(() => this.db.medias.get(mediaId));
      if (!row || row.blobData === undefined) return;
      const { blobData: _confirmed, ...meta } = row;
      await this.db.withRetry(() => this.db.medias.put(meta));
    } catch {
      // Sans conséquence : le dégonflage rétroactif repassera dessus.
    }
  }

  private async markConflict(
    b: OfflineQueueRecord,
    serverTask: Task,
    reason: string,
    serverRender?: TaskRender,
  ) {
    this.observer.onConflict?.({ kind: b.payload.kind, taskId: b.taskId, reason });
    await this.db.withRetry(async () => {
      // La prochaine ouverture du formulaire repart de l'état serveur,
      // pas du draft périmé qui vient d'être refusé.
      if (serverRender) {
        await this.db.renders.put({
          taskId: serverTask.id,
          userId: this.userId,
          data: serverRender,
          updatedAt: new Date().toISOString(),
        });
      }
      await this.db.queue.update(b.id, {
        status: "conflict",
        lastError: reason,
        updatedAt: new Date().toISOString(),
      });
      const prev = await this.db.tasks.get(serverTask.id);
      await this.db.tasks.put({
        id: serverTask.id,
        userId: this.userId,
        data: serverTask,
        ...(prev?.supervisor ? { supervisor: prev.supervisor } : {}),
        updatedAt: new Date().toISOString(),
        localStatus: "conflict",
      });
      // Drop autres mutations en attente pour cette task : elles ne s'appliqueront plus.
      const others = await this.db.queue
        .where("taskId")
        .equals(b.taskId)
        .and((q) => q.userId === this.userId && q.status === "pending")
        .toArray();
      await this.db.queue.bulkPut(
        others.map((o) => ({
          ...o,
          status: "purged" as const,
          lastError: "task conflict",
          updatedAt: new Date().toISOString(),
        })),
      );
    });
  }

  private async markErrored(b: OfflineQueueRecord, message: string) {
    const nextRetry = b.retryCount + 1;
    const purged = nextRetry >= MAX_RETRY;
    await this.db.withRetry(() =>
      this.db.queue.update(b.id, {
        status: purged ? "purged" : "pending",
        retryCount: nextRetry,
        lastError: message,
        updatedAt: new Date().toISOString(),
      }),
    );
    if (purged) {
      this.observer.onPurge?.({
        kind: b.payload.kind,
        taskId: b.taskId,
        retryCount: nextRetry,
        reason: message,
      });
    }
  }

  private async markBatchFailed(batch: OfflineQueueRecord[], message: string) {
    await this.db.withRetry(() =>
      this.db.queue.bulkPut(
        batch.map((b) => ({
          ...b,
          status: b.retryCount + 1 >= MAX_RETRY ? "purged" : "pending",
          retryCount: b.retryCount + 1,
          lastError: message,
          updatedAt: new Date().toISOString(),
        })),
      ),
    );
    for (const b of batch) {
      const nextRetry = b.retryCount + 1;
      if (nextRetry >= MAX_RETRY) {
        this.observer.onPurge?.({
          kind: b.payload.kind,
          taskId: b.taskId,
          retryCount: nextRetry,
          reason: message,
        });
      }
    }
  }

  // ── Retry sync for one task (UI "Réessayer" CTA) ─────────────────────
  async retrySyncForTask(taskId: string): Promise<void> {
    const now = new Date().toISOString();

    const medias = await this.db.withRetry(() =>
      this.db.medias
        .where("taskId")
        .equals(taskId)
        .and((m) => m.userId === this.userId && !m.uploaded)
        .toArray(),
    );
    for (const m of medias) {
      await this.db.withRetry(() =>
        this.db.medias.update(m.id, {
          uploadRetryCount: 0,
          uploadLastError: undefined,
          uploadLastAttemptAt: undefined,
          updatedAt: now,
        }),
      );
    }

    const queueRows = await this.db.withRetry(() =>
      this.db.queue
        .where("taskId")
        .equals(taskId)
        .and(
          (q) =>
            q.userId === this.userId &&
            (q.status === "purged" || q.retryCount > 0) &&
            q.lastError !== SUPERSEDED_ERROR,
        )
        .toArray(),
    );
    for (const q of queueRows) {
      await this.db.withRetry(() =>
        this.db.queue.update(q.id, {
          status: "pending",
          retryCount: 0,
          lastError: undefined,
          updatedAt: now,
        }),
      );
    }
  }

  // ── Dégonflage rétroactif des blobs déjà uploadés ────────────────────

  private reclaimKeys: string[] | null = null;
  private reclaimCursor = 0;
  private reclaimFinished = false;

  private reclaimMetaKey(): string {
    return `media-blob-reclaim:${this.userId}`;
  }

  /**
   * Libère le base64 des photos DÉJÀ montées côté serveur, par petits lots.
   *
   * Les appareils en service portent des mois de photos uploadées dont le blob
   * n'a jamais été effacé (la fuite corrigée dans le flush d'upload) — jusqu'à
   * 8 Go relevés en prod, au point que le backend IndexedDB de WebKit rendait
   * `UnknownError` et que plus aucune écriture ne passait. Un correctif qui ne
   * s'applique qu'aux nouvelles photos les laisserait wedgés pour toujours.
   *
   * Incrémental et HORS transaction de version, délibérément : une migration
   * Dexie réécrirait tout le store d'un bloc (des Go d'I/O dans une seule
   * transaction) et son échec rendrait la base impossible à ouvrir — l'app
   * serait morte, pas seulement lente. Ici chaque lot est indépendant, une
   * ligne qui casse n'arrête pas le balayage, et une session interrompue
   * reprend au démarrage suivant.
   *
   * `primaryKeys()` marche sur un curseur de CLÉS : aucune valeur lue, donc son
   * coût ne dépend pas du poids des blobs. Seuls les `get` du lot courant
   * paient, et ils sont bornés.
   */
  async reclaimUploadedBlobs(batchSize = RECLAIM_BATCH_SIZE): Promise<number> {
    if (this.reclaimFinished) return 0;

    if (this.reclaimKeys === null) {
      const done = await this.db.withRetry(() =>
        this.db.meta.get(this.reclaimMetaKey()),
      );
      if (done) {
        this.reclaimFinished = true;
        return 0;
      }
      this.reclaimKeys = (await this.db.withRetry(() =>
        this.db.medias.where("userId").equals(this.userId).primaryKeys(),
      )) as string[];
      this.reclaimCursor = 0;
    }

    // Enregistrements de médias encore en suspens : tant qu'une mutation
    // `register_media` traîne dans la file (pending, failed, conflict ou
    // purged), on ignore ce que dit le drapeau `uploaded` — la photo n'est pas
    // rattachée au rendu et sa copie locale reste la seule preuve exploitable.
    const keys = this.reclaimKeys;
    const unsettled = new Set<string>(
      (
        await this.db.withRetry(() =>
          this.db.queue
            .where("taskId")
            .anyOf([...new Set(keys.map((k) => k.slice(0, k.indexOf(":"))))])
            .and((q) => q.userId === this.userId && q.kind === "register_media")
            .toArray(),
        )
      ).flatMap((q) =>
        q.payload.kind === "register_media"
          ? [`${q.taskId}:${q.payload.media.client_id}`]
          : [],
      ),
    );

    let freed = 0;
    const end = Math.min(this.reclaimCursor + batchSize, keys.length);
    for (; this.reclaimCursor < end; this.reclaimCursor++) {
      const key = keys[this.reclaimCursor];
      if (key === undefined) continue;
      if (unsettled.has(key)) continue;
      try {
        const row = await this.db.withRetry(() => this.db.medias.get(key));
        // Trois conditions cumulatives, volontairement strictes : montée,
        // chemin de stockage connu, et blob encore présent. Une ligne
        // incohérente (uploaded sans storagePath) est laissée intacte — on
        // préfère du stockage gaspillé à une photo de chantier détruite.
        if (!row || !row.uploaded || !row.storagePath) continue;
        if (row.blobData === undefined) continue;
        const { blobData: _reclaimed, ...meta } = row;
        await this.db.withRetry(() => this.db.medias.put(meta));
        freed++;
      } catch {
        // Une ligne illisible ne doit pas bloquer le dégonflage des autres :
        // c'est précisément sur une base en souffrance que ce balayage tourne.
      }
    }

    if (this.reclaimCursor >= keys.length) {
      this.reclaimFinished = true;
      this.reclaimKeys = null;
      await this.db.withRetry(() =>
        this.db.meta.put({
          key: this.reclaimMetaKey(),
          value: { doneAt: new Date().toISOString() },
        }),
      );
    }
    return freed;
  }

  // ── One-shot sync cycle (used by the loop and the "Synchroniser" CTA) ─
  async syncNow(): Promise<{
    uploads: UploadSweepResult;
    flush: FlushResult;
  }> {
    await this.refreshFromServer();
    await this.refreshCatalogue("vt-upsell");
    // Règles « un fait de VT → une option » du cockpit : « La VT propose ».
    await this.refreshCatalogue("vt-upsell-rules");
    const uploads = await this.uploadPendingForSubmittedTasks();
    const flush = await this.flush();
    // En dernier : le dégonflage est du confort de stockage, il ne doit jamais
    // retarder la remontée de données terrain.
    await this.reclaimUploadedBlobs().catch(() => 0);
    return { uploads, flush };
  }

  // ── Helpers expose ───────────────────────────────────────────────────
  async clearForUser(): Promise<void> {
    await this.db.withRetry(async () => {
      await this.db.tasks.where("userId").equals(this.userId).delete();
      await this.db.renders.where("userId").equals(this.userId).delete();
      await this.db.medias.where("userId").equals(this.userId).delete();
      await this.db.queue.where("userId").equals(this.userId).delete();
    });
  }
}
